import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import CtyPromptInput, { ErrorBoundary, promptInputType, UploadedFileType, CommandFormPromptInput, isCompactApplicationType, useScreenWidth, uploadAttachmentFiles } from '@circuitry-ai/doc-ui';
import { RootState, SETTING_TYPE, updateUploadedImageData, updateCurrentChatMentionedEntities, useAppDispatch, useGetAiworkersPresignedUrlMutation, useUploadFileToS3Mutation } from '@circuitry-ai/doc-data';
import { Icon } from '@iconify/react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

// Add types for ImportMeta to fix linter error
declare global {
  interface ImportMeta {
    env: Record<string, string>;
  }
}

interface ChatFormProps {
  handleSendClick: (userInput: any, imageMeta?: { sources: { source_id: string; file_name: string }[] }, attachedImageFiles?: File[]) => void;
  setIsSpeechMode: (value: boolean) => void;
  isSpeechMode: boolean;
  messagesLength: number;
  disabled?: boolean;
  onVoiceAgentConnectionChange?: (connected: boolean) => void;
  onVoiceAgentTranscriptLine?: (line: { role: 'user' | 'assistant'; text: string }) => void;
  commandFormDataRef?: React.MutableRefObject<{ formData: Record<string, string>; customMessage: string } | null>;
  /** PAL view: limits the `/` agent list to agents enabled for PAL. */
  palView?: boolean;
}

const CtyPromptInputWithErrorBoundary = ErrorBoundary(CtyPromptInput);

const ChatForm: React.FC<ChatFormProps> = ({
  handleSendClick,
  isSpeechMode,
  setIsSpeechMode,
  messagesLength,
  disabled = false,
  onVoiceAgentConnectionChange,
  onVoiceAgentTranscriptLine,
  commandFormDataRef,
  palView = false,
}) => {
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFileType[]>([]);
  const focusRef = useRef<HTMLTextAreaElement>(null);
  const { advisorAbilities } = useSelector((state: RootState) => state.advisor);
  const isInputDisabled = disabled;
  const dispatch = useAppDispatch();
  const { uploadedImageData, currentChatMentionedEntities } = useSelector((state: RootState) => state.chatAdvisorSliceReducer);
  const { t } = useTranslation();
  const [getAiworkersPresignedUrl] = useGetAiworkersPresignedUrlMutation();
  const [uploadToS3] = useUploadFileToS3Mutation();
  const screenWidth = useScreenWidth();
  const useStackedPromptPadding = isCompactApplicationType() || screenWidth < 768;

  const agentCommandJson = useMemo(() => {
    if (currentChatMentionedEntities && 'commandJson' in currentChatMentionedEntities) {
      return (currentChatMentionedEntities as any).commandJson as Record<string, any> | undefined;
    }
    return undefined;
  }, [currentChatMentionedEntities]);

  const [commandFormData, setCommandFormData] = useState<Record<string, string>>({});
  const [commandCustomMessage, setCommandCustomMessage] = useState('');

  useEffect(() => {
    if (!agentCommandJson) {
      setCommandFormData({});
      setCommandCustomMessage('');
    }
  }, [agentCommandJson]);

  useEffect(() => {
    if (commandFormDataRef) {
      commandFormDataRef.current = agentCommandJson
        ? { formData: commandFormData, customMessage: commandCustomMessage }
        : null;
    }
  }, [commandFormData, commandCustomMessage, agentCommandJson, commandFormDataRef]);

  const handleFilesSelected = useCallback(
    (files: File[]) =>
      uploadAttachmentFiles(files, {
        getPresignedUrl: getAiworkersPresignedUrl,
        uploadToS3,
        setUploadedFiles,
        errorMessage: t('Upload failed'),
      }),
    [getAiworkersPresignedUrl, uploadToS3, t]
  );


  useEffect(() => {
    if (messagesLength > 0) {
      focusRef.current?.focus();
    }
  }, [messagesLength]);
  useEffect(() => {
    return () => {
      dispatch(updateUploadedImageData(''));
    };
  }, [])

  const handleRemoveCommandAgent = () => {
    dispatch(updateCurrentChatMentionedEntities({}));
  };

  if (agentCommandJson) {
    return (
      <div className="flex min-h-0 w-full flex-col gap-1 overflow-hidden">
        <div className="shrink-0 bg-gradient-to-r from-blue-50 to-purple-50 border border-primary rounded-full px-3 py-1 flex justify-between items-center gap-2">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center">
              <Icon icon="mingcute:robot-2-fill" width="10" height="10" className="text-white" />
            </div>
            <span className="text-sm font-medium text-primary">/{(currentChatMentionedEntities as any)?.name}</span>
          </div>
          <Icon icon="zondicons:close-solid" width="20" height="20"
            onClick={handleRemoveCommandAgent} className="cursor-pointer text-primary" />
        </div>
        <CommandFormPromptInput
          commandJson={agentCommandJson}
          formData={commandFormData}
          customMessage={commandCustomMessage}
          onFormDataChange={setCommandFormData}
          onCustomMessageChange={setCommandCustomMessage}
          onSend={() => handleSendClick('')}
          aiworkerId={(currentChatMentionedEntities as any)?.aiworker_id}
          isDisabled={isInputDisabled}
          fillAvailableHeight
        />
      </div>
    );
  }

  return (
    <div
      className="flex w-full flex-col items-start rounded-medium bg-content1 transition-colors hover:bg-content1 mb-1 border chat-converse-input relative"
    >
      <CtyPromptInputWithErrorBoundary
        elementType={promptInputType.TEXTAREA}
        id='chatinput-textarea'
        classNames={{
          inputWrapper: `!bg-transparent shadow-none ${uploadedImageData ? '!border-none !rounded-none' : ''} `,
          innerWrapper: "relative",
          // Mobile / widget: send pinned in endContent. Desktop web absolute toolbar needs more bottom padding.
          input: useStackedPromptPadding
            ? "py-1 pl-2 !pr-10 text-medium"
            : "pt-1 pb-6 pl-2 !pr-10 text-medium",
        }}
        radius="lg"
        variant="flat"
        ref={focusRef}
        handleSendClick={handleSendClick}
        placeholder="Start typing a question..."
        enableTranslation={advisorAbilities?.[SETTING_TYPE.TRANSLATION]?.is_active ?? false}
        enableSpeak={advisorAbilities?.[SETTING_TYPE.SPEAK]?.is_active ?? false}
        enableImage={advisorAbilities?.[SETTING_TYPE.IMAGES]?.is_active ?? false}
        enableFile={advisorAbilities?.[SETTING_TYPE.FILE_INPUT]?.is_active ?? false}
        enableSendBtn={true}
        setIsSpeechMode={setIsSpeechMode}
        isSpeechMode={isSpeechMode}
        enableMention={true}
        palView={palView}
        enableLiveChat={advisorAbilities?.[SETTING_TYPE.LIVE_CHAT]?.is_active ?? false}
        isDisabled={isInputDisabled}
        uploadedFiles={uploadedFiles}
        setUploadedFiles={setUploadedFiles}
        onFilesSelected={handleFilesSelected}
        onVoiceAgentConnectionChange={onVoiceAgentConnectionChange}
        onVoiceAgentTranscriptLine={onVoiceAgentTranscriptLine}
        enableConversationRegionSelection={true}
      />
    </div>
  );
};

export default ChatForm;